/** Barra inferior de ARMAS (10.4I). Slot 1 = arma equipada; os demais são a CORRENTE, mostrando
 * ONDE desbloqueiam (por nível) e o último é VIP. A ARMADURA não aparece aqui — é gerida na Bolsa
 * (onde reflete ao trocar). Config: equipment.weaponSlots.
 * 10.9J7 tirou o slot VIP; 10.9L0 ele VOLTOU (decisão do Thiago: vantagem de dano do VIP/Passe). */
import { GameConfig } from "../config/GameConfig";
import { i18n } from "../i18n/i18n";
import { rarityCss } from "../items/Rarity";
import { itemSvg, slotSvg } from "../items/icons";
import { Vip } from "../progression/Vip";
import type { Inventory } from "../items/Inventory";
import type { EquipmentSlot, EquipmentInstance } from "../items/EquipmentTypes";

const SLOTS = GameConfig.equipment.weaponSlots;
const LOCK_SVG = `<svg viewBox="0 0 24 24" width="100%" height="100%" style="display:block;opacity:0.55"><path d="M8 11 V8 a4 4 0 0 1 8 0 V11" fill="none" stroke="currentColor" stroke-width="1.8"/><rect x="6.5" y="11" width="11" height="8.5" rx="2" fill="currentColor"/></svg>`;
const VIP_SVG = `<svg viewBox="0 0 24 24" width="100%" height="100%" style="display:block"><path d="M4 8 L8 12 L12 5 L16 12 L20 8 L18 18 L6 18 Z" fill="#ffcf4d" stroke="#c98a1a" stroke-width="1"/></svg>`;

export class EquipmentBar {
  private readonly root: HTMLDivElement;

  constructor(uiRoot: HTMLElement, private readonly onOpen: (slot?: EquipmentSlot) => void, private readonly onInfo?: (msg: string) => void) {
    this.root = document.createElement("div");
    this.root.className = "equip-bar";
    this.root.hidden = true;
    let html = "";
    for (let i = 0; i < SLOTS.length; i++) {
      html += `<button class="eq-slot" data-i="${i}"><span class="eq-ic"></span><span class="eq-lv"></span><span class="eq-tag" hidden></span></button>`;
    }
    this.root.innerHTML = html;
    uiRoot.appendChild(this.root);
    this.root.querySelectorAll<HTMLButtonElement>(".eq-slot").forEach((btn) => {
      btn.onclick = () => this.handleTap(Number(btn.dataset.i));
    });
  }

  show(): void { this.root.hidden = false; }
  hide(): void { this.root.hidden = true; }

  private playerLevel = 1;

  private handleTap(i: number): void {
    const cfg = SLOTS[i];
    if (i === 0 || cfg.unlock === "start") { this.onOpen("weapon"); return; }
    if (cfg.unlock === "vip") { this.onInfo?.(i18n.t(Vip.weaponSlotUnlocked ? "weaponSlot.vipActive" : "weaponSlot.vip")); return; } // 10.9L0
    if (cfg.unlock === "level") {
      this.onInfo?.(this.playerLevel >= cfg.level ? i18n.t("weaponSlot.soon") : i18n.t("weaponSlot.unlockLevel", { level: cfg.level }));
    }
  }

  /** `chain` (10.5H): armas presas pela corrente — ocupam os slots 1..4 quando existem. */
  update(inv: Inventory, playerLevel = 1, chain: EquipmentInstance[] = []): void {
    this.playerLevel = playerLevel;
    // 10.9L0 — quantos slots de NÍVEL já abriram: a arma que sobra na corrente (a do slot VIP) aparece
    // NO slot VIP, e não no próximo slot de nível ainda fechado (antes a extra "pulava" pra lá)
    const levelOpen = SLOTS.filter((s, idx) => idx >= 1 && s.unlock === "level" && playerLevel >= s.level).length;
    const slots = this.root.querySelectorAll<HTMLButtonElement>(".eq-slot");
    slots.forEach((btn, i) => {
      const cfg = SLOTS[i];
      const ic = btn.querySelector<HTMLElement>(".eq-ic")!;
      const lv = btn.querySelector<HTMLElement>(".eq-lv")!;
      const tag = btn.querySelector<HTMLElement>(".eq-tag")!;
      btn.hidden = false; // 10.9EB
      btn.classList.remove("locked", "filled", "vip", "ready", "mainhand");
      btn.style.borderColor = "";
      btn.style.removeProperty("--rq");
      tag.hidden = true; lv.textContent = "";

      // slots 1..N: arma presa pela corrente (se houver). Slot de nível aberto = arma de índice i-1
      // (os de nível abrem em ordem); slot VIP = a arma seguinte às dos slots de nível.
      const idx = cfg.unlock === "vip" ? (Vip.weaponSlotUnlocked ? levelOpen : -1)
        : cfg.unlock === "level" && playerLevel >= cfg.level ? i - 1 : -1;
      if (i >= 1 && idx >= 0 && chain[idx]) {
        const cw = chain[idx];
        ic.innerHTML = itemSvg(cw); lv.textContent = `L${cw.level}`;
        btn.style.borderColor = rarityCss(cw.rarity);
        btn.style.setProperty("--rq", rarityCss(cw.rarity)); // 10.6C: halo na cor da raridade
        btn.classList.add("filled");
        if (cfg.unlock === "vip") btn.classList.add("vip");
        tag.textContent = cfg.unlock === "vip" ? "VIP⛓" : "⛓"; tag.hidden = false;
        return;
      }
      if (cfg.unlock === "vip") { // 10.9L0 — slot VIP vazio: liberado (VIP✓) ou trancado (VIP)
        const vip = Vip.weaponSlotUnlocked;
        ic.innerHTML = VIP_SVG; btn.classList.add("vip", vip ? "ready" : "locked");
        tag.textContent = vip ? "VIP✓" : "VIP"; tag.hidden = false;
        return;
      }

      if (i === 0 || cfg.unlock === "start") {
        // slot ativo: a arma equipada
        const inst = inv.equipped.weapon;
        if (inst) {
          ic.innerHTML = itemSvg(inst);
          lv.textContent = `L${inst.level}`;
          btn.style.borderColor = rarityCss(inst.rarity);
          btn.style.setProperty("--rq", rarityCss(inst.rarity)); // 10.6C
          btn.classList.add("filled", "mainhand"); // 10.6C: marca a MÃO PRINCIPAL
          tag.textContent = i18n.t("weaponSlot.mainHand");
          tag.hidden = false;
        } else { ic.innerHTML = slotSvg("weapon"); }
        return;
      }
      if (playerLevel >= cfg.level) {
        // 10.9EC — os slots VOLTAM (o 10.9EB escondia os que so diziam "Em breve"; decisao do
        // Thiago: a barra mostra o loadout inteiro, mesmo o que ainda nao abriu).
        ic.innerHTML = slotSvg("weapon"); btn.classList.add("ready");
        tag.textContent = i18n.t("weaponSlot.soonShort"); tag.hidden = false;
      } else {
        ic.innerHTML = LOCK_SVG; btn.classList.add("locked");
        tag.textContent = `Nv${cfg.level}`; tag.hidden = false;
      }
    });
  }
}
